import type { NavItemId, ShellNavItemDefinition } from "@lifter/hub-ui/browser-shared";
import {
  BarChart3,
  BookOpen,
  Bot,
  CircleUserRound,
  ClipboardCheck,
  Coins,
  LayoutDashboard,
  LifeBuoy,
  MessagesSquare,
  PanelsTopLeft,
  Settings,
  Users,
} from "lucide-react";
import { RouterLink } from "../components/ui/router-link";

const NAV_ICONS: Record<NavItemId, typeof LayoutDashboard> = {
  dashboard: LayoutDashboard,
  approvals: ClipboardCheck,
  workspace: MessagesSquare,
  specialists: Users,
  skills: BookOpen,
  automations: Bot,
  dashboards: BarChart3,
  applications: PanelsTopLeft,
  usage: Coins,
  account: LifeBuoy,
  settings: Settings,
  "settings-people": CircleUserRound,
};

/**
 * One sidebar/drawer navigation entry. Lives in its own module (not
 * PortalShell.tsx) so the navigation-links test harness can mount it without
 * bundling the whole shell.
 */
export function NavItem({
  activeNav,
  item,
  onSelect,
  count,
}: {
  activeNav: NavItemId | null;
  item: ShellNavItemDefinition;
  onSelect?: () => void;
  count?: number;
}) {
  const Icon = NAV_ICONS[item.id];
  const active = item.id === activeNav;

  return (
    <li>
      {/* RouterLink so Cmd/Ctrl/Shift/middle-click open a new tab natively.
          `onSelect` (the drawer close) rides `onNavigated`, not onClick: it must
          fire only on a plain-click SPA navigation — a modified click keeps the
          current page, so the drawer should stay open. */}
      {/* Emphasis is carried by weight, ink and fill together: the resting row
          stays quiet so the selected one reads without the fill doing all the
          work. Icon tint tracks the row rather than a blanket opacity, so it
          gains contrast on hover instead of staying washed out. */}
      <RouterLink
        aria-current={active ? "page" : undefined}
        className={[
          "group flex items-center gap-(--space-3) rounded-(--radius-md) px-(--space-3) py-(--space-2) leading-tight touch-manipulation",
          "[transition:background-color_var(--motion-fast),color_var(--motion-fast)]",
          active
            ? "type-body-strong bg-(--brand-soft) text-(--brand-dark)"
            : "type-body text-(--color-text-secondary) hover:bg-(--color-surface-2) hover:text-(--color-text)",
        ].join(" ")}
        href={item.href}
        onNavigated={onSelect}
      >
        <Icon
          aria-hidden="true"
          className={[
            "size-4 shrink-0 [transition:color_var(--motion-fast)]",
            active
              ? "text-(--brand)"
              : "text-(--color-text-tertiary) group-hover:text-(--color-text-secondary)",
          ].join(" ")}
          strokeWidth={1.6}
        />
        <span className="min-w-0 truncate">{item.label}</span>
        {count !== undefined && count > 0 ? (
          <span className="type-meta ml-auto flex h-(--space-5) min-w-(--space-5) shrink-0 items-center justify-center rounded-full bg-(--brand) px-(--space-1) font-semibold text-(--brand-contrast)">
            <span aria-hidden="true">{count}</span>
            <span className="sr-only">{count} awaiting your review</span>
          </span>
        ) : null}
      </RouterLink>
    </li>
  );
}
